import { useEffect, useState, useCallback } from "react";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Phone, EnvelopeSimple, Sparkle, Copy } from "@phosphor-icons/react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", notes: "", tags: [] };

const RISK_STYLE = {
  low: "bg-[#7A8B5C]/15 text-[#5d6b46]",
  medium: "bg-[#D4AF37]/20 text-[#9a7d1e]",
  high: "bg-[#B86B5A]/15 text-[#B86B5A]",
};

export default function Customers() {
  const { currency } = useAuth();
  const { t, lang } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, id: null });
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState({ open: false, customer: null, loading: false, data: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/customers", { params: search ? { search } : {} });
    setCustomers(data);
  }, [search]);

  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
  }, [load]);

  const openNew = () => { setForm(empty); setDialog({ open: true, id: null }); };
  const openEdit = (c) => { setForm({ name: c.name, email: c.email || "", phone: c.phone || "", notes: c.notes || "", tags: c.tags || [] }); setDialog({ open: true, id: c.id }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error(t.cust.nameReq); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (dialog.id) await api.put(`/customers/${dialog.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(dialog.id ? t.cust.updated : t.cust.added);
      setDialog({ open: false, id: null });
      load();
    } catch (e) { toast.error(t.dlg.failed); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/customers/${id}`); toast.success(t.cust.removed); load(); };

  const openAi = async (c) => {
    setAi({ open: true, customer: c, loading: true, data: null });
    try {
      const { data } = await api.post(`/customers/${c.id}/ai-insights`, { lang });
      setAi({ open: true, customer: c, loading: false, data: data.insights });
    } catch (e) {
      setAi({ open: false, customer: null, loading: false, data: null });
      toast.error(formatApiError(e.response?.data?.detail) || t.custai.error);
    }
  };

  const copyMsg = (msg) => { navigator.clipboard?.writeText(msg); toast.success(t.custai.copied); };
  const riskLabel = (r) => ({ low: t.custai.low, medium: t.custai.medium, high: t.custai.high }[r] || r);

  return (
    <div className="fade-up space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.cust.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.cust.title}</h1>
        </div>
        <button data-testid="new-customer-btn" onClick={openNew}
          className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> {t.cust.add}
        </button>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A67]" />
        <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.cust.searchPh}
          className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition text-sm" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {customers.map((c) => (
          <div key={c.id} data-testid={`customer-card-${c.id}`} className="bg-white rounded-xl border border-[#E5E2DC] p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm group">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-[#2C3E35] text-white flex items-center justify-center text-sm font-bold">
                  {c.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-xs text-[#6B6A67]">{c.total_visits || 0} {t.cust.visits} · {money(c.total_spent, currency)}</div>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={15} /></button>
                <button data-testid={`del-customer-${c.id}`} onClick={() => del(c.id)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={15} /></button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-xs text-[#6B6A67]">
              {c.email && <div className="flex items-center gap-2"><EnvelopeSimple size={14} /> {c.email}</div>}
              {c.phone && <div className="flex items-center gap-2"><Phone size={14} /> {c.phone}</div>}
            </div>
            {c.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.tags.map((tag) => <span key={tag} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#B86B5A]/12 text-[#B86B5A]">{tag}</span>)}
              </div>
            )}
            <button data-testid={`ai-insights-${c.id}`} onClick={() => openAi(c)}
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/8 text-[#9a7d1e] text-xs font-semibold hover:bg-[#D4AF37]/16 transition-colors">
              <Sparkle size={14} weight="fill" /> {t.custai.btn}
            </button>
          </div>
        ))}
        {customers.length === 0 && <div className="col-span-full py-16 text-center text-[#6B6A67] text-sm">{t.cust.none}</div>}
      </div>

      <Modal open={dialog.open} onClose={() => setDialog({ open: false, id: null })} testid="customer-dialog"
        title={dialog.id ? t.cust.edit : t.cust.addTitle}
        footer={<><GhostBtn onClick={() => setDialog({ open: false, id: null })}>{t.btn.cancel}</GhostBtn><PrimaryBtn onClick={save} disabled={saving} data-testid="customer-save">{saving ? t.btn.saving : t.btn.save}</PrimaryBtn></>}>
        <div className="space-y-4">
          <Field label={t.cust.fName}><input data-testid="customer-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Olivia Bennett" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.cust.fEmail}><input data-testid="customer-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="olivia@email.com" /></Field>
            <Field label={t.cust.fPhone}><input data-testid="customer-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+506 0000 0000" /></Field>
          </div>
          <Field label={t.cust.fTags}><input data-testid="customer-tags" className={inputCls} value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="VIP, Regular" /></Field>
          <Field label={t.cust.fNotes}><textarea data-testid="customer-notes" className={inputCls} rows={2} value={form.notes} onChange={set("notes")} /></Field>
        </div>
      </Modal>

      <Modal open={ai.open} onClose={() => setAi({ open: false, customer: null, loading: false, data: null })} testid="ai-insights-dialog" size="lg"
        title={`${t.custai.title}${ai.customer ? " · " + ai.customer.name : ""}`}
        footer={<GhostBtn onClick={() => setAi({ open: false, customer: null, loading: false, data: null })}>{t.custai.close}</GhostBtn>}>
        {ai.loading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-center" data-testid="ai-insights-loading">
            <div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#D4AF37] animate-spin" />
            <p className="text-sm text-[#6B6A67]">{t.custai.loading}</p>
          </div>
        ) : ai.data ? (
          <div className="space-y-5" data-testid="ai-insights-content">
            <div>
              <p className="label-caps mb-1">{t.custai.summary}</p>
              <p className="text-sm text-[#3a3a38] leading-relaxed">{ai.data.summary}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="shrink-0">
                <p className="label-caps mb-1">{t.custai.churn}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${RISK_STYLE[ai.data.churn_risk] || RISK_STYLE.medium}`}>{riskLabel(ai.data.churn_risk)}</span>
              </div>
              {ai.data.churn_reason && <p className="text-sm text-[#6B6A67] leading-relaxed pt-5">{ai.data.churn_reason}</p>}
            </div>
            {ai.data.next_visit_suggestion && (
              <div>
                <p className="label-caps mb-1">{t.custai.next}</p>
                <p className="text-sm text-[#3a3a38] leading-relaxed">{ai.data.next_visit_suggestion}</p>
              </div>
            )}
            {Array.isArray(ai.data.upsell_ideas) && ai.data.upsell_ideas.length > 0 && (
              <div>
                <p className="label-caps mb-1.5">{t.custai.upsell}</p>
                <div className="flex flex-wrap gap-2">
                  {ai.data.upsell_ideas.map((u, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#2C3E35]/8 text-[#2C3E35]">{u}</span>
                  ))}
                </div>
              </div>
            )}
            {ai.data.rebooking_message && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="label-caps">{t.custai.message}</p>
                  <button data-testid="copy-rebooking-msg" onClick={() => copyMsg(ai.data.rebooking_message)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#B86B5A] hover:underline">
                    <Copy size={13} /> {t.custai.copy}
                  </button>
                </div>
                <div className="rounded-lg bg-[#F5F2ED] border border-[#E5E2DC] p-4 text-sm text-[#3a3a38] leading-relaxed">{ai.data.rebooking_message}</div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
